import dynamicTextUtilities from './dynamicTextUtilities'
import preserveStyling from 'utils/tinyMCE/updateDynamicText/preserveStyling'
import { load } from 'cheerio'
import preserveItalicsAndBolds from 'utils/tinyMCE/updateDynamicText/preserveItalicsAndBolds'
import formatDate from 'utils/dates/formatDate'
import addSpansToParagraphs from 'utils/tinyMCE/updateDynamicText/addSpansToParagraphs'
import { extractInlineStyles } from 'utils/tinyMCE/updateDynamicText/extractInlineStyles'
import { escapeHtml, sanitizeCaseRichTextField } from '@messenger/utils'
import { buildAdditionalFamilyContent } from 'utils/tinyMCE/caseDetails/buildAdditionalFamilyContent'
import { formFieldKeys } from '@messenger/consts'
import { arrangeNameWrapper } from './arrangeNameWrapper'
import {
  PLAIN_TEXT_BLOCK_PATHS,
  plainTextBlockToHtml,
} from 'utils/tinyMCE/caseDetails/plainTextBlockHtml'

/** The block fields that hold rich text: the case's obituary and order of service. */
const RICH_TEXT_PATHS = new Set(['obituary', formFieldKeys.orderOfService])

export const updateCaseDetails = (dynamicTextProps) => {
  const { type, element } = dynamicTextProps.elementProps
  // A rich-text field stored in a span (an obituary inserted before it
  // became a block) whose value carries markup merges as the block it is,
  // so its HTML is never painted as text. A plain-text value keeps the span's
  // text path, so it paints exactly as it always has.
  const isRichTextSpan =
    type === 'span' &&
    RICH_TEXT_PATHS.has(element?.attr?.('data-case-detail')?.split(' ')?.[0]) &&
    carriesMarkup(
      dynamicTextUtilities(dynamicTextProps).getCaseDetail(element).value,
    )
  if (type === 'span' && !isRichTextSpan)
    handleSimpleCaseDetail(dynamicTextProps)
  if (isRichTextSpan) takeParagraphPlace(element)
  if (type === 'div' || isRichTextSpan) handleCaseDetailBlock(dynamicTextProps)
}

/** A complete element tag (opening, closing or self-closing) or a comment. */
const MARKUP = /<\/?[a-zA-Z][a-zA-Z0-9:-]*(?:\s[^<>]*)?\/?>|<!--/

/**
 * Whether a case value holds markup, as an HTML or XAML obituary does: a
 * complete tag whose name is an element name (letters, digits, `-` and `:`,
 * starting with a letter), opening, closing or self-closing, or a comment.
 * Plain text with a `<` followed by neither a letter, `/` nor `!--`
 * (`aged < 5`, `I <3 you`), a `<` with no closing `>` (`Mom<Dad`) or an
 * address or URL in angle brackets (`<jane@example.com>`) does not, so it
 * keeps the text path whole. A word of letters and digits in angle brackets
 * (`<Bud>`), or one followed by a space and more text (`<Big Bud's>`), still
 * reads as a tag.
 *
 * @param {unknown} value a case value
 * @returns {boolean} whether it holds a tag or a comment
 */
export const carriesMarkup = (value) =>
  typeof value === 'string' && MARKUP.test(value)

/** Inline wrappers TinyMCE puts around a formatted case-detail span. */
const INLINE_WRAPPERS = new Set(['strong', 'b', 'em', 'i', 'u', 'span'])

/** The declarations of a style attribute, without a trailing `;`. */
const declarations = (style) => (style || '').trim().replace(/;+$/, '')

/** Whether every node of `parent` but `child` is whitespace text or a `<br>`. */
const holdsOnly = (parent, child) =>
  parent
    .contents()
    .toArray()
    .every(
      (node) =>
        node === child.get(0) ||
        (node.type === 'text' && /^[\s\u200a\u200b]*$/.test(node.data)) ||
        (node.type === 'tag' && node.name === 'br'),
    )

/**
 * A rich-text span alone in its paragraph, directly or through inline
 * wrappers that hold nothing else, takes the paragraph's place, so the block
 * it becomes never sits inside a `<p>`, where a browser would split the
 * paragraph and drop its alignment and size. The paragraph's and the
 * wrappers' styles move onto the span, a bold or italic wrapper as
 * `font-weight` or `font-style` and an underline wrapper as `text-decoration`.
 * A span that shares its paragraph stays in it and still takes those
 * styles, so the block a browser splits out of the paragraph keeps its
 * alignment and size.
 */
const takeParagraphPlace = (element) => {
  const styles = [declarations(element.attr('style'))]
  let alone = true
  let child = element
  let parent = element.parent()
  while (parent.length && INLINE_WRAPPERS.has(parent.get(0).name)) {
    if (!holdsOnly(parent, child)) alone = false
    const name = parent.get(0).name
    if (name === 'strong' || name === 'b') styles.unshift('font-weight: bold')
    if (name === 'em' || name === 'i') styles.unshift('font-style: italic')
    if (name === 'u') styles.unshift('text-decoration: underline')
    styles.unshift(declarations(parent.attr('style')))
    child = parent
    parent = parent.parent()
  }
  if (!parent.is('p')) return
  if (!holdsOnly(parent, child)) alone = false
  styles.unshift(declarations(parent.attr('style')))
  const style = styles.filter(Boolean).join('; ')
  if (style) element.attr('style', `${style};`)
  if (alone) parent.replaceWith(element)
}

const handleSimpleCaseDetail = (dynamicTextProps) => {
  const { elementProps, editorProps } = dynamicTextProps
  const { element } = elementProps
  const { dateFormat, pageSetLocale } = editorProps

  const { getCaseDetail, updateDeepestSpanText } =
    dynamicTextUtilities(dynamicTextProps)

  const { value, type, path } = getCaseDetail(element)

  // The name wrapper follows the case's arrangement when the case has one;
  // its parts then take their values in this same pass.
  const arrangement = editorProps.caseDetails?.name_arrangement
  if (path === 'name_of_deceased' && arrangement)
    if (arrangeNameWrapper(element, arrangement, editorProps.caseDetails))
      return

  let updatedValue = value
  if (type === 'empty') updatedValue = '\u00A0'
  if (type === 'date')
    updatedValue = formatDate(value, dateFormat, false, pageSetLocale)
  if (type === 'emptyName') updatedValue = '\u200A'

  updateDeepestSpanText(element, updatedValue)
}

const handleCaseDetailBlock = (dynamicTextProps) => {
  const { elementProps, editorProps } = dynamicTextProps
  const { element } = elementProps

  const { getCaseDetail } = dynamicTextUtilities(dynamicTextProps)
  const { path, value } = getCaseDetail(element)

  // Additional family blocks hold an array of members, not a string —
  // rebuild the member lines from current case data so sidebar edits
  // (add/remove/rename) refresh the inserted block.
  const isAdditionalFamilyBlock = path === formFieldKeys.additionalFamily

  // MIP-synced obituaries arrive as Silverlight XAML (<Section><Paragraph><Run>).
  // Inlined as-is, the text lands inside <run> elements with no font of their
  // own, so TinyMCE's FontName/Bold can't restyle a selection in it (the font
  // change silently no-ops). Convert XAML → editable HTML so the block behaves
  // like any other text. The rich-text fields are inlined through the case
  // write's per-field step (sanitizeCaseRichTextField), which converts XAML
  // only when no text is lost and cuts every value to the case rich-text
  // policy, since a row stored before that policy may hold anything; a
  // plain-text block takes one paragraph per line; any other field is plain
  // text and is inlined escaped.
  let resolvedValue
  if (isAdditionalFamilyBlock) {
    resolvedValue = buildAdditionalFamilyContent(
      editorProps.caseDetails?.[formFieldKeys.additionalFamily],
    )
  } else if (RICH_TEXT_PATHS.has(path)) {
    resolvedValue = sanitizeCaseRichTextField(String(value ?? '')).html
  } else if (PLAIN_TEXT_BLOCK_PATHS.has(path)) {
    resolvedValue = plainTextBlockToHtml(value)
  } else {
    resolvedValue = escapeHtml(String(value ?? ''))
  }

  const divTemplate = `<div class="mceNonEditable" data-case-detail="${escapeHtml(String(path))}">${resolvedValue}</div>`
  const templateElement = load(divTemplate)

  preserveStyling({
    initialElement: element,
    templateElement: templateElement,
  })

  preserveItalicsAndBolds({
    initialElement: element,
    targetElement: templateElement,
    targetTag: 'p',
    findLast: false,
  })

  const divElement = templateElement('div').first()
  const divStyles = divElement.attr('style') || ''

  const { formattedString } = extractInlineStyles(
    divStyles,
    ['font-size', 'font-family'],
    'font-size: 11pt;',
  )

  const caseDetailBlock = addSpansToParagraphs(
    templateElement.html(),
    formattedString,
  )

  element.replaceWith(caseDetailBlock)
}

export default updateCaseDetails
